// src/hooks/usePublicUtilitiesIntel.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Public Utilities v2 intelligence state
// --- UPDATE (Public Utilities v2) — NEW FILE ------------------------------------------------
// Loads, once per page:
//   overview  (risk, compliance, zones, managers, hotspots, recommendations, per-asset index)
//   managers  (who manages assets)       zones + constituency codes        catalog (types, templates)
// Live: listens to `assets_imported` and `asset_lifecycle_update` on the WebSocket and to the
// page's own refresh signal; bursts are coalesced into one reload (2.5 s) and skipped while the
// browser tab is hidden (caught up when it becomes visible again).
// --- END UPDATE ------------------------------------------------------------------------------
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import webSocketService from '../services/websocketService';
import * as api from '../services/publicUtilitiesApi';

const COALESCE_MS = 2500;
const LIVE_EVENTS = ['assets_imported', 'asset_lifecycle_update',
    // --- UPDATE (Public Utilities v3): managers / zones / codes / assignment changes and status changes made
    //     anywhere in FlowSense (Digital Twin, tech app, Work Orders) refresh the intelligence too
    'public_utilities_update', 'asset_status_changed'];
// socket envelopes differ ({ type, payload } · { data } · raw) — always hand tabs the inner object
const unwrapEvent = (d) => (d && typeof d === 'object' ? (d.payload && typeof d.payload === 'object' ? d.payload : d.data && typeof d.data === 'object' && !Array.isArray(d.data) ? d.data : d) : {});
// --- END UPDATE

export default function usePublicUtilitiesIntel({ enabled = true, onAssetsChanged } = {}) {
    const [overview, setOverview] = useState(null);
    const [managers, setManagers] = useState([]);
    const [zones, setZones] = useState({ zones: [], codes: [] });
    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastEvent, setLastEvent] = useState(null);
    const timerRef = useRef(null);
    const pendingRef = useRef(false);
    const aliveRef = useRef(true);
    const onChangedRef = useRef(onAssetsChanged);
    useEffect(() => { onChangedRef.current = onAssetsChanged; }, [onAssetsChanged]);

    const loadAll = useCallback(async ({ refresh = false } = {}) => {
        setError(null);
        const [o, m, z, c] = await Promise.allSettled([api.getOverview(refresh), api.listManagers(), api.getZones(refresh), catalog ? Promise.resolve(catalog) : api.getCatalog()]);
        if (!aliveRef.current) return;
        if (o.status === 'fulfilled') setOverview(o.value);
        if (m.status === 'fulfilled') setManagers(Array.isArray(m.value) ? m.value : []);
        if (z.status === 'fulfilled') setZones(z.value || { zones: [], codes: [] });
        if (c.status === 'fulfilled' && c.value) setCatalog(c.value);
        const failed = [o, m, z].filter((r) => r.status === 'rejected');
        if (failed.length === 3) setError(failed[0].reason?.message || 'Public Utilities intelligence is unavailable.');
        setLoading(false);
    }, [catalog]);

    const loadRef = useRef(loadAll);
    useEffect(() => { loadRef.current = loadAll; }, [loadAll]);

    const scheduleRefresh = useCallback((reason) => {
        pendingRef.current = true;
        if (timerRef.current) return;
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return; // caught up on visibilitychange
            pendingRef.current = false;
            loadRef.current({ refresh: true }).catch(() => {});
            if (reason !== 'local') onChangedRef.current?.(reason);
        }, COALESCE_MS);
    }, []);

    useEffect(() => {
        aliveRef.current = true;
        if (!enabled) return undefined;
        loadRef.current().catch((e) => { setError(e.message); setLoading(false); });
        const handlers = LIVE_EVENTS.map((name) => {
            const h = (raw) => {
                // --- UPDATE (v3): normalised payload + the asset ids it touches (Asset 360 refreshes itself)
                const payload = unwrapEvent(raw);
                const assetIds = [payload.asset_id ?? payload.assetId, ...(Array.isArray(payload.asset_ids) ? payload.asset_ids : [])].filter((x) => x !== undefined && x !== null).map(String);
                setLastEvent({ name, payload, assetIds, kind: payload.kind || payload.scope || null, at: Date.now() });
                // --- END UPDATE
                scheduleRefresh(name);
            };
            try { webSocketService.subscribe(name, h); } catch (_) { /* socket optional */ }
            return [name, h];
        });
        const onVisible = () => { if (document.visibilityState === 'visible' && pendingRef.current) scheduleRefresh('visible'); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            aliveRef.current = false;
            handlers.forEach(([name, h]) => { try { webSocketService.unsubscribe(name, h); } catch (_) { /* ignore */ } });
            document.removeEventListener('visibilitychange', onVisible);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = null;
        };
    }, [enabled, scheduleRefresh]);

    /** Per-asset intelligence (risk, completeness, next due …) for O(1) lookups in tables. */
    const intelById = useMemo(() => new Map((overview?.assets_index || []).map((a) => [String(a.id), a])), [overview]);
    const managerById = useMemo(() => new Map(managers.map((m) => [String(m.id), m])), [managers]);
    const zoneById = useMemo(() => new Map((zones.zones || []).map((z) => [String(z.id), z])), [zones]);

    const refresh = useCallback(() => loadRef.current({ refresh: true }), []);
    const refreshSoon = useCallback(() => scheduleRefresh('local'), [scheduleRefresh]);
    // one stable object per data change, so memoised tabs skip re-rendering when the page re-renders
    return useMemo(() => ({
        overview, managers, zones: zones.zones || [], codes: zones.codes || [], catalog, loading, error, lastEvent,
        intelById, managerById, zoneById, refresh, refreshSoon, setManagers,
    }), [overview, managers, zones, catalog, loading, error, lastEvent, intelById, managerById, zoneById, refresh, refreshSoon]);
}
